/**
 * InventorySummaryPanel - Displays inventory counts by denomination
 * Shows State/Federal tab toggle and summary table with visual enhancements
 */

const TABS = [
  { key: 'S', label: 'State' },
  { key: 'F', label: 'Federal' },
];

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
});

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '--';
  return currencyFormatter.format(value);
};

const InventorySummaryPanel = ({
  mainAgency,
  selectedSubAgency,
  activeTab,
  onTabChange,
}) => {
  const mainInventory = (mainAgency?.inventory || []).filter((i) => i.powerTypeCd === activeTab);
  const subInventory = (selectedSubAgency?.inventory || []).filter((i) => i.powerTypeCd === activeTab);

  // Build merged rows by powerAmountCd
  const amountCds = new Set([
    ...mainInventory.map((i) => i.powerAmountCd),
    ...subInventory.map((i) => i.powerAmountCd),
  ]);

  const rows = Array.from(amountCds)
    .map((cd) => {
      const main = mainInventory.find((i) => i.powerAmountCd === cd);
      const sub = subInventory.find((i) => i.powerAmountCd === cd);
      return {
        powerAmountCd: cd,
        powerAmountDescription: main?.powerAmountDescription || sub?.powerAmountDescription || '',
        powerAmountValue: main?.powerAmountValue || sub?.powerAmountValue || 0,
        mainCount: main?.count || 0,
        subCount: sub?.count || 0,
      };
    })
    .sort((a, b) => a.powerAmountValue - b.powerAmountValue);

  const totalMain = rows.reduce((sum, r) => sum + r.mainCount, 0);
  const totalSub = rows.reduce((sum, r) => sum + r.subCount, 0);

  const tabLabel = activeTab === 'S' ? 'State' : 'Federal';
  const maxCount = Math.max(...rows.map((r) => Math.max(r.mainCount, r.subCount)), 1);

  return (
    <div>
      {/* Tab toggle */}
      <div role="tablist" aria-label="Power type" className="flex gap-1 mb-4 bg-gray-100 dark:bg-gray-700 rounded-lg p-1 w-fit">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            role="tab"
            aria-selected={activeTab === tab.key}
            onClick={() => onTabChange(tab.key)}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
              activeTab === tab.key
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Summary table */}
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
          No {tabLabel} powers in inventory.
        </p>
      ) : (
        <>
          {/* Prominent total header */}
          <div className="flex items-baseline gap-3 mb-3">
            <div>
              <span className="text-2xl font-bold tabular-nums text-gray-900 dark:text-white">{totalMain}</span>
              <span className="text-sm text-gray-500 dark:text-gray-400 ml-1.5">{tabLabel} Powers</span>
            </div>
            {selectedSubAgency && (
              <div>
                <span className="text-2xl font-bold tabular-nums text-orange-600 dark:text-orange-400">{totalSub}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400 ml-1.5">Sub-Agency</span>
              </div>
            )}
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm min-w-[300px]" aria-label={`${tabLabel} power inventory summary`}>
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-3 sm:px-4 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Power Amount
                  </th>
                  <th className="px-3 sm:px-4 py-2.5 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Your Count
                  </th>
                  {selectedSubAgency && (
                    <th className="px-3 sm:px-4 py-2.5 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Sub-Agency
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map((row, index) => (
                  <tr
                    key={row.powerAmountCd}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/20 ${
                      index % 2 === 0 ? 'bg-white dark:bg-transparent' : 'bg-gray-50/50 dark:bg-gray-800/20'
                    }`}
                  >
                    <td className="px-3 sm:px-4 py-2 text-gray-900 dark:text-white">
                      <span className="text-sm sm:text-base font-semibold">{formatCurrency(row.powerAmountValue)}</span>
                      {row.powerAmountDescription && (
                        <span className="text-gray-500 dark:text-gray-400 ml-1.5 sm:ml-2 text-xs hidden sm:inline">({row.powerAmountDescription})</span>
                      )}
                    </td>
                    <td className="px-3 sm:px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-1.5 sm:gap-2">
                        <div className="w-10 sm:w-16 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all"
                            style={{ width: `${(row.mainCount / maxCount) * 100}%` }}
                          />
                        </div>
                        <span className="font-semibold tabular-nums text-gray-900 dark:text-white">{row.mainCount}</span>
                      </div>
                    </td>
                    {selectedSubAgency && (
                      <td className="px-3 sm:px-4 py-2 text-right">
                        <div className="flex items-center justify-end gap-1.5 sm:gap-2">
                          <div className="w-10 sm:w-16 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-orange-400 rounded-full transition-all"
                              style={{ width: `${(row.subCount / maxCount) * 100}%` }}
                            />
                          </div>
                          <span className="font-semibold tabular-nums text-gray-900 dark:text-white">{row.subCount}</span>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-primary-50/30 dark:bg-primary-900/10 border-t-2 border-primary-100 dark:border-primary-800">
                <tr>
                  <td className="px-3 sm:px-4 py-2 font-semibold text-gray-900 dark:text-white">Total</td>
                  <td className="px-3 sm:px-4 py-2 text-right text-primary font-bold tabular-nums">{totalMain}</td>
                  {selectedSubAgency && (
                    <td className="px-3 sm:px-4 py-2 text-right text-orange-600 dark:text-orange-400 font-bold tabular-nums">{totalSub}</td>
                  )}
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default InventorySummaryPanel;
